/**
 * The other rooms of a floor plan, as the viewer draws them around the room in
 * focus — the pure geometry: where each of their walls stands on the floor, and
 * whether a wall is in the way of the view into the focus room.
 *
 * Everything is in the viewer's world x / z, where the focus room is centred on
 * the origin: it spans [-length/2, length/2] × [-width/2, width/2].
 *
 * No three.js here, so it is unit-testable.
 */

import { ContextRoom, WallFace } from './types';

export interface FloorPoint { x: number; z: number }
export type FloorSegment = [FloorPoint, FloorPoint];

export const WALL_FACES: WallFace[] = ['front', 'right', 'back', 'left'];

/** A context room's footprint in world x / z, given the focus room's size. */
export function contextFootprint(room: ContextRoom, focusLength: number, focusWidth: number) {
  const x0 = -focusLength / 2 + room.offsetX;
  const z0 = -focusWidth / 2 + room.offsetZ;
  return { x0, z0, x1: x0 + room.length, z1: z0 + room.width };
}

/** Where each wall of a context room stands on the floor. */
export function contextWallSegments(room: ContextRoom, focusLength: number, focusWidth: number): Record<WallFace, FloorSegment> {
  const { x0, z0, x1, z1 } = contextFootprint(room, focusLength, focusWidth);
  return {
    front: [{ x: x0, z: z0 }, { x: x1, z: z0 }],
    back: [{ x: x0, z: z1 }, { x: x1, z: z1 }],
    left: [{ x: x0, z: z0 }, { x: x0, z: z1 }],
    right: [{ x: x1, z: z0 }, { x: x1, z: z1 }],
  };
}

const cross = (o: FloorPoint, a: FloorPoint, b: FloorPoint) => (a.x - o.x) * (b.z - o.z) - (a.z - o.z) * (b.x - o.x);

function inTriangle(p: FloorPoint, a: FloorPoint, b: FloorPoint, c: FloorPoint): boolean {
  const d1 = cross(a, b, p);
  const d2 = cross(b, c, p);
  const d3 = cross(c, a, p);
  const neg = d1 < 0 || d2 < 0 || d3 < 0;
  const pos = d1 > 0 || d2 > 0 || d3 > 0;
  return !(neg && pos);
}

function segmentsCross(p1: FloorPoint, p2: FloorPoint, q1: FloorPoint, q2: FloorPoint): boolean {
  const d1 = cross(q1, q2, p1);
  const d2 = cross(q1, q2, p2);
  const d3 = cross(p1, p2, q1);
  const d4 = cross(p1, p2, q2);
  return ((d1 > 0 && d2 < 0) || (d1 < 0 && d2 > 0)) && ((d3 > 0 && d4 < 0) || (d3 < 0 && d4 > 0));
}

function segmentMeetsTriangle(seg: FloorSegment, a: FloorPoint, b: FloorPoint, c: FloorPoint): boolean {
  return inTriangle(seg[0], a, b, c) || inTriangle(seg[1], a, b, c)
    || segmentsCross(seg[0], seg[1], a, b) || segmentsCross(seg[0], seg[1], b, c) || segmentsCross(seg[0], seg[1], c, a);
}

/**
 * Is this wall between the camera and the focus room? True when the wall
 * crosses the region swept from the camera to the focus room's footprint —
 * some line of sight into the room passes through it. From inside the focus
 * room nothing outside it is in the way.
 */
export function wallHidesFocusRoom(wall: FloorSegment, camera: FloorPoint, focusLength: number, focusWidth: number): boolean {
  const hx = focusLength / 2;
  const hz = focusWidth / 2;
  if (Math.abs(camera.x) <= hx && Math.abs(camera.z) <= hz) return false;
  const corners: FloorPoint[] = [{ x: -hx, z: -hz }, { x: hx, z: -hz }, { x: hx, z: hz }, { x: -hx, z: hz }];
  for (let i = 0; i < 4; i++) {
    if (segmentMeetsTriangle(wall, camera, corners[i], corners[(i + 1) % 4])) return true;
  }
  return false;
}

/** Which context room's floor is under a point on the ground, if any. */
export function contextRoomAt(rooms: ContextRoom[], point: FloorPoint, focusLength: number, focusWidth: number): ContextRoom | null {
  for (const room of rooms) {
    const f = contextFootprint(room, focusLength, focusWidth);
    if (point.x >= f.x0 && point.x <= f.x1 && point.z >= f.z0 && point.z <= f.z1) return room;
  }
  return null;
}
